import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import {
  assistantReducer, initialAssistantState, MAX_SESSIONS, openAssistantResult,
  type AssistantAction, type AssistantOpenRequest, type AssistantState,
} from '@/features/ai-chat/assistantSessions'
import { clearSnapshot, loadSnapshot, restoreMessages, saveSnapshot, type StoredSession } from '@/features/ai-chat/chatSessionStorage'
import { CHAT_SNAPSHOTS_CLEARED_EVENT } from '@/features/ai-chat/chatSessionClear'
import type { ChatMessage } from '@/features/ai-chat/types'
import { useAppSelector } from '@/store/hooks'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { DOCK_MIN_VIEWPORT, DOCK_WIDTH } from '@/features/ai-chat/windowGeometry'
import { loadDockMode, saveDockMode, type DockMode } from '@/features/ai-chat/dockModeStorage'
import { CONVERSATION_LINK_PARAM } from '@/features/ai-chat/agent/conversationLink'
import { AIChatErrorBoundary } from './AIChatErrorBoundary'
import { AssistantMinimizedBar } from './AssistantMinimizedBar'
import { AssistantWindowContext, type AssistantAttention, type AssistantWindowInternalApi, type ConversationSnapshot, type ConversationStatus } from './assistantWindowContext'

/** 消息变化后多久写一次 sessionStorage（流式 token 期间不必每个 token 都写，§8.4） */
const SAVE_THROTTLE_MS = 500

export { useAssistantWindow } from './assistantWindowContext'
export type { AssistantWindowApi, AssistantAttention } from './assistantWindowContext'

// 浮窗本体(AgentConversation → evals / agentic 契约 / aiApiSupport …)只在打开后才渲染,
// 按需加载,不进入口 chunk。Provider 挂在全局 Layout 上,静态 import 会把整套 AI 代码
// 打进每个页面的首屏包。
const AssistantWindow = lazy(() => import('./AssistantWindow').then((m) => ({ default: m.AssistantWindow })))

const newSessionId = (): string => {
  try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID() } catch { /* fall through */ }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const sameStatus = (a: ConversationStatus | undefined, b: ConversationStatus) =>
  !!a && a.streaming === b.streaming && a.awaitingConfirmation === b.awaitingConfirmation && a.unread === b.unread && a.failed === b.failed && a.canReset === b.canReset

export function AssistantWindowProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('chrome')
  // 状态放 ref 同步推进：同一 tick 里连续 openAssistant（多个入口 / 双击）也按最新状态判上限
  const stateRef = useRef<AssistantState>(initialAssistantState)
  const [state, setState] = useState<AssistantState>(initialAssistantState)
  const apply = useCallback((action: AssistantAction) => {
    const next = assistantReducer(stateRef.current, action)
    if (next !== stateRef.current) { stateRef.current = next; setState(next) }
  }, [])

  const userIdForMode = useAppSelector(st => st.user.userId ?? null)
  const [dockMode, setDockModeState] = useState<DockMode>(() => loadDockMode(userIdForMode))
  // 用户 id 到位（或换人）后读这个人的选择
  useEffect(() => { setDockModeState(loadDockMode(userIdForMode)) }, [userIdForMode])
  const setDockMode = useCallback((mode: DockMode) => { setDockModeState(mode); saveDockMode(userIdForMode, mode) }, [userIdForMode])
  const wideEnough = useMediaQuery(`(min-width: ${DOCK_MIN_VIEWPORT}px)`)

  const [rawStatuses, setStatuses] = useState<Record<string, ConversationStatus>>({})
  const resetters = useRef(new Map<string, () => void>())
  const triggerRef = useRef<HTMLElement | null>(null)

  const onStatus = useCallback((sessionId: string, status: ConversationStatus) => {
    setStatuses(prev => (sameStatus(prev[sessionId], status) ? prev : { ...prev, [sessionId]: status }))
  }, [])
  const onRegisterReset = useCallback((sessionId: string, reset: (() => void) | null) => {
    if (reset) resetters.current.set(sessionId, reset)
    else resetters.current.delete(sessionId)
  }, [])
  const resetSession = useCallback((sessionId: string) => { resetters.current.get(sessionId)?.() }, [])

  // 已关掉的会话不再算状态（卸载时的最后一次上报可能晚于移除）；原始表随会话列表修剪，不越积越多
  const statuses = useMemo(() => {
    const live: Record<string, ConversationStatus> = {}
    for (const s of state.sessions) if (rawStatuses[s.id]) live[s.id] = rawStatuses[s.id]
    return live
  }, [state.sessions, rawStatuses])
  useEffect(() => {
    const ids = new Set(state.sessions.map(s => s.id))
    setStatuses(prev => (Object.keys(prev).every(id => ids.has(id)) ? prev : Object.fromEntries(Object.entries(prev).filter(([id]) => ids.has(id)))))
  }, [state.sessions])

  const openAssistant = useCallback((request: AssistantOpenRequest) => {
    const result = openAssistantResult(stateRef.current, request)
    if (result === 'limit') {
      toast.error(t('aiChat.window.tabLimit', { max: MAX_SESSIONS }))
      return result
    }
    apply({ type: 'open', request, newId: newSessionId() })
    return result
  }, [apply, t])
  const minimize = useCallback(() => apply({ type: 'minimize' }), [apply])
  const activate = useCallback((sessionId: string) => apply({ type: 'activate', id: sessionId }), [apply])
  const restore = useCallback(() => apply({ type: 'restore' }), [apply])

  // 页面专用助手（openAssistant 的 page）：离开它的页面，前台换成通用助手；窗口最小化时也换，恢复时看到的就是通用助手
  const { pathname } = useLocation()
  const pathnameRef = useRef(pathname)
  useLayoutEffect(() => { pathnameRef.current = pathname }, [pathname])
  const syncPage = useCallback(() => apply({ type: 'syncPage', pathname: pathnameRef.current, newId: newSessionId() }), [apply])
  useEffect(() => { syncPage() }, [pathname, syncPage])
  // ---- 刷新恢复（F3 阶段 4，§8 方案 A）----
  const userId = useAppSelector(s => s.user.userId ?? null)
  // 事件回调（pagehide / 节流写）读最新 userId；layout effect 里同步，不在 render 期写 ref
  const userIdRef = useRef(userId)
  useLayoutEffect(() => { userIdRef.current = userId }, [userId])
  const restoredRef = useRef(new Map<string, { conversationId: string; messages: ChatMessage[] }>())
  const liveRef = useRef(new Map<string, ConversationSnapshot>())
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const saveNow = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null }
    const uid = userIdRef.current
    if (!uid) return
    const st = stateRef.current
    if (st.windowState === 'closed') { clearSnapshot(uid); return }
    const sessions: StoredSession[] = []
    const alive = new Set(st.sessions.map(s => s.id))
    for (const id of liveRef.current.keys()) if (!alive.has(id)) liveRef.current.delete(id) // 已关掉的会话
    for (const s of st.sessions) {
      const live = liveRef.current.get(s.id)
      if (!live) continue
      sessions.push({
        id: s.id, entry: s.entry, ...(s.subject ? { subject: s.subject } : {}), ...(s.hint ? { hint: s.hint } : {}),
        ...(s.page ? { page: s.page } : {}),
        conversationId: live.conversationId, messages: live.messages, inFlight: live.streaming, ...(live.turn ? { turn: live.turn } : {}),
      })
    }
    saveSnapshot(uid, { windowState: st.windowState, activeId: st.activeId, sessions })
  }, [])
  const scheduleSave = useCallback(() => {
    if (!saveTimer.current) saveTimer.current = setTimeout(saveNow, SAVE_THROTTLE_MS)
  }, [saveNow])

  const onPersist = useCallback((sessionId: string, snapshot: ConversationSnapshot) => {
    liveRef.current.set(sessionId, snapshot)
    scheduleSave()
  }, [scheduleSave])
  const getRestored = useCallback((sessionId: string) => restoredRef.current.get(sessionId), [])

  // 窗口开合 / 会话增减也要写（关着 = 删）
  useEffect(() => { scheduleSave() }, [state, scheduleSave])
  // 刷新 / 关标签前补写一次：流进行中的那一轮要带着 inFlight 存下来
  useEffect(() => {
    window.addEventListener('pagehide', saveNow)
    window.addEventListener('beforeunload', saveNow)
    return () => {
      window.removeEventListener('pagehide', saveNow)
      window.removeEventListener('beforeunload', saveNow)
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null }
    }
  }, [saveNow])

  // 用户落地（刷新后 /current/info 可能晚到）→ 读回本用户的快照；换用户 / 登出 → 清掉上一个用户的
  const prevUserRef = useRef<string | null>(null)
  useEffect(() => {
    const prev = prevUserRef.current
    prevUserRef.current = userId
    if (prev === userId) return
    if (prev) {
      clearSnapshot(prev)
      liveRef.current.clear()
      restoredRef.current.clear()
      apply({ type: 'closeWindow' })
    }
    if (!userId) return
    const snap = loadSnapshot(userId)
    if (!snap || !snap.sessions.length) return
    const now = Date.now()
    restoredRef.current = new Map(snap.sessions.map(s => [s.id, { conversationId: s.conversationId, messages: restoreMessages(s, now) }]))
    const sessions = snap.sessions.map(({ id, entry, subject, hint, page }) =>
      ({ id, entry, ...(subject ? { subject } : {}), ...(hint ? { hint } : {}), ...(page ? { page } : {}) }))
    const activeId = sessions.some(s => s.id === snap.activeId) ? snap.activeId : sessions[0].id
    apply({ type: 'hydrate', state: { windowState: snap.windowState, sessions, activeId } })
    // 刷新后落在别的页面：恢复出来的页面助手也不留在前台
    syncPage()
  }, [userId, apply, syncPage])

  // 会话链接（P3e「复制链接」）：/?assistant_conversation=<id> → 在窗口里开这个会话，再把参数从地址栏去掉（刷新不重复开）。
  // 放在读回快照之后：先恢复原有标签，再开这一个。能不能看由服务端判（主人或被分享人），别人打开是 404。
  useEffect(() => {
    if (!userId) return
    const url = new URL(window.location.href)
    const id = url.searchParams.get(CONVERSATION_LINK_PARAM)
    if (!id || !/^\d+$/.test(id)) return
    url.searchParams.delete(CONVERSATION_LINK_PARAM)
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    openAssistant({ entry: 'agent_chat', subject: { type: 'conversation', id, label: t('aiChat.agent.linkedConversation') } })
  }, [userId, openAssistant, t])

  const closeWindow = useCallback(() => {
    apply({ type: 'closeWindow' })
    // 关闭 = 结束会话：立刻删，别等节流（紧接着刷新也不能恢复）
    liveRef.current.clear()
    restoredRef.current.clear()
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null }
    clearSnapshot(userIdRef.current)
  }, [apply])

  const closeSession = useCallback((sessionId: string) => {
    restoredRef.current.delete(sessionId)
    liveRef.current.delete(sessionId)
    apply({ type: 'closeSession', id: sessionId })
    // 关掉的是最后一个：等同关窗，立刻删快照
    if (stateRef.current.windowState === 'closed') {
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null }
      clearSnapshot(userIdRef.current)
    }
  }, [apply])

  // 登出（authApi.logout → clearAllChatSnapshots）：浮窗跟着关，登出跳转时的 pagehide 就只会删、不会把对话写回来
  useEffect(() => {
    window.addEventListener(CHAT_SNAPSHOTS_CLEARED_EVENT, closeWindow)
    return () => window.removeEventListener(CHAT_SNAPSHOTS_CLEARED_EVENT, closeWindow)
  }, [closeWindow])

  // 开发模式调试钩子：本期没有页面入口调用 openAssistant（C0 09-14），live 走查多标签 / 上下文头靠它；生产构建里整段被裁掉
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const w = window as unknown as { __novaAssistant?: { openAssistant: typeof openAssistant } }
    w.__novaAssistant = { openAssistant }
    return () => { delete w.__novaAssistant }
  }, [openAssistant])

  // 焦点：打开 / 恢复 → 进当前会话的输入框；最小化 / 关闭 → 回到顶栏按钮（§4 键盘可访问性）
  const prevWindowState = useRef(state.windowState)
  useEffect(() => {
    const prev = prevWindowState.current
    prevWindowState.current = state.windowState
    if (prev === state.windowState) return
    if (state.windowState === 'open') {
      // 输入框在回复中是禁用的，focus 不上：落到浮窗本身，Esc 仍归浮窗（从顶栏按钮恢复时焦点不能留在按钮上）
      const textarea = document.querySelector<HTMLTextAreaElement>('[data-assistant-window] [data-active] textarea')
      if (textarea && !textarea.disabled) textarea.focus()
      else document.querySelector<HTMLElement>('[data-assistant-window]')?.focus()
    } else if (prev === 'open') {
      triggerRef.current?.focus()
    }
  }, [state.windowState])

  const all = Object.values(statuses)
  const attention: AssistantAttention = all.some(s => s.awaitingConfirmation) ? 'awaitingConfirmation'
    : all.some(s => s.streaming) ? 'replying'
      : all.some(s => s.failed) ? 'replyFailed'
        : all.some(s => s.unread) ? 'newReply' : null
  const needsCloseConfirm = all.some(s => s.streaming || s.awaitingConfirmation)

  const docked = dockMode === 'dock' && wideEnough && state.windowState === 'open'
  // 页面上别的浮层（Flyout）读这个变量，让出停靠面板的宽度：停靠时 flyout 出在面板左边，对话和详情能同时看
  const dockInset = docked ? DOCK_WIDTH : 0
  useEffect(() => {
    const root = document.documentElement
    if (dockInset) root.style.setProperty('--assistant-dock-inset', `${dockInset}px`)
    else root.style.removeProperty('--assistant-dock-inset')
    return () => { root.style.removeProperty('--assistant-dock-inset') }
  }, [dockInset])
  const api: AssistantWindowInternalApi = {
    dockMode, setDockMode, canDock: wideEnough, docked, dockInset: docked ? DOCK_WIDTH : 0,
    state, openAssistant, minimize, restore, closeWindow, attention, needsCloseConfirm, activate, closeSession,
    statuses, onStatus, onRegisterReset, resetSession, triggerRef, getRestored, onPersist,
  }

  return (
    <AssistantWindowContext.Provider value={api}>
      {children}
      {state.windowState !== 'closed' && (
        // 浮窗崩了只吞掉浮窗，不波及页面
        // 崩了收回 closed：边界随之卸载，下次打开是全新的一份（review 09-13）
        <AIChatErrorBoundary onError={closeWindow}>
          <Suspense fallback={null}>
            <AssistantWindow />
          </Suspense>
          {state.windowState === 'minimized' && <AssistantMinimizedBar />}
        </AIChatErrorBoundary>
      )}
    </AssistantWindowContext.Provider>
  )
}
